import { useState, useEffect, useContext } from "react";
import { useSpring, animated } from "react-spring";
import {
  Layout,
  Row,
  Col,
  Card,
  Modal,
  Button,
  Table,
  Typography,
  Select,
  Empty,
  message,
} from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  DollarOutlined,
  FileProtectOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { DashboardSkeleton } from "../../components/SkeletonLoaders";
import ChartDrilldownModal, {
  ChartDrilldownSummary,
} from "../../components/ModalForm/Dashboard/ChartDrilldownModal";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
} from "recharts";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";

import {
  getDashboardDrilldownApi,
  getDashboardReportApi,
  DashboardDrilldownChart,
  DashboardDrilldownRow,
  SalesByGroupRow,
} from "../../services/Report/ReportApis";

const mapSalesByGroupRows = (raw: SalesByGroupRow[] | any[]) =>
  [...raw]
    .map((g: any) => {
      const groupLevel =
        g.groupLevel != null && g.groupLevel !== ""
          ? Number(g.groupLevel)
          : undefined;
      const baseName = String(
        g.groupName ?? g.name ?? `Group ${g.groupId ?? g.id}`
      );
      return {
        name:
          groupLevel != null && !Number.isNaN(groupLevel)
            ? `${baseName} (L${groupLevel})`
            : baseName,
        policyCount: Number(g.policyCount ?? g.count ?? 0),
        salesAmount: Number(g.salesAmount ?? g.sales ?? 0),
        key: String(g.groupId ?? g.id ?? g.groupName ?? g.name),
        groupLevel,
      };
    })
    .sort((a, b) => b.salesAmount - a.salesAmount || b.policyCount - a.policyCount);

const { Title: AntTitle, Text } = Typography;
const { Content } = Layout;

const DashboardPage: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const { handleStart, handleClose } = useContext(TourContext);

  const [isActive, setActive] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [datePeriod, setDatePeriod] = useState("last14");

  const [barData, setBarData] = useState<{ name: string; value: number; paid: number; key?: string }[]>([]);
  const [collectionsData, setCollectionsData] = useState<{ name: string; amount: number; key?: string }[]>([]);
  const [claimsData, setClaimsData] = useState<{ name: string; internalCollected: number; internalClaimed: number; externalCollected: number; externalClaimed: number }[]>([]);
  const [hiddenClaimSeries, setHiddenClaimSeries] = useState<Set<string>>(new Set());
  const [pieData, setPieData] = useState<{ name: string; value: number; key: string }[]>([]);
  const [coverAmountPieData, setCoverAmountPieData] = useState<{ name: string; value: number; key: string }[]>([]);
  const [salesByGroupData, setSalesByGroupData] = useState<
    {
      name: string;
      policyCount: number;
      salesAmount: number;
      key: string;
      groupLevel?: number;
    }[]
  >([]);
  const [hasSalesByGroup, setHasSalesByGroup] = useState(false);
  const [totalSales, setTotalSales] = useState(0);
  const [activePolicyCount, setActivePolicyCount] = useState(0);
  const [salesChange, setSalesChange] = useState(0);
  const [policyChange, setPolicyChange] = useState(0);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [activeProductCount, setActiveProductCount] = useState(0);
  const [topProducts, setTopProducts] = useState<any[]>([]);

  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState("");
  const [drilldownSummary, setDrilldownSummary] =
    useState<ChartDrilldownSummary | null>(null);
  const [drilldownRows, setDrilldownRows] = useState<DashboardDrilldownRow[]>(
    []
  );
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);

  const pctOf = (value: number, total: number) =>
    total > 0 ? (value / total) * 100 : 0;

  const pieLabel =
    (total: number) =>
    ({ name, value }: { name?: string; value?: number }) => {
      const v = Number(value ?? 0);
      const pct = pctOf(v, total);
      return `${name ?? ""}: ${v} (${pct.toFixed(1)}%)`;
    };

  const openChartDrilldown = async (opts: {
    chart: DashboardDrilldownChart;
    key: string;
    label: string;
    count: number;
    total: number;
    extra?: string;
    title: string;
  }) => {
    const summary: ChartDrilldownSummary = {
      label: opts.label,
      count: opts.count,
      percent: pctOf(opts.count, opts.total),
      extra: opts.extra,
    };
    setDrilldownTitle(opts.title);
    setDrilldownSummary(summary);
    setDrilldownRows([]);
    setDrilldownError(null);
    setDrilldownOpen(true);
    setDrilldownLoading(true);

    try {
      const res = await getDashboardDrilldownApi({
        period: datePeriod,
        groupId: currentGroup,
        chart: opts.chart,
        key: opts.key,
      });
      const rows = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.rows)
          ? res.data.rows
          : [];
      setDrilldownRows(rows);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load member rows for this bucket.";
      setDrilldownError(msg);
      message.warning("Unable to load chart drilldown.");
    } finally {
      setDrilldownLoading(false);
    }
  };

  const openPieBucketDrilldown = (
    chart: "age" | "cover",
    buckets: { name: string; value: number; key: string }[],
    bucketName: string,
    titlePrefix: string
  ) => {
    const row = buckets.find((b) => b.name === bucketName);
    if (!row) return;
    const total = buckets.reduce((s, d) => s + d.value, 0);
    void openChartDrilldown({
      chart,
      key: row.key,
      label: row.name,
      count: row.value,
      total,
      title: `${titlePrefix} — ${row.name}`,
    });
  };

  const handleOk = () => {
    setIsModalVisible(false);

    handleStart();
  };

  const handleCancel = () => {
    setIsModalVisible(false);

    handleClose();
  };

  const AnimatedNumber = ({ value }: { value: number }) => {
    const { number } = useSpring({
      from: { number: 0 },
      to: { number: value },
      config: { duration: 500 },
    });

    return (
      <animated.span>
        {number.to((n) =>
          n.toLocaleString(undefined, { maximumFractionDigits: 0 })
        )}
      </animated.span>
    );
  };

  useEffect(() => {
    if (currentUser && currentUser.FirstLogin) {
      const { Dashboard } = JSON.parse(
        localStorage.getItem("TourModal") || "{}"
      );
      if (!Dashboard) {
        setIsModalVisible(true);
      }
    }
  }, [currentUser]);

  const pieColors = ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1", "#13c2c2"];
// For simplicity, using a fixed set of colors. In production, consider a more robust approach for many categories.
  const productColumns = [
    { title: "#", dataIndex: "score" },
    { title: "Product Name", dataIndex: "name" },
    { title: "Policy Premium", dataIndex: "price" },
    { title: "Cover Amount", dataIndex: "cover" },
    { title: "Sales", dataIndex: "qty" },
    { title: "Sales Volume", dataIndex: "volume" },
    { title: "Profit Margin", dataIndex: "margin" },
  ];

  const CustomRoundedBar = (props: any) => {
    const { x, y, width, height, fill } = props;
    const radius = 15;

    if (height < radius) {
      // Draw a plain rectangle (or a very small rounded top only)
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={height > 0 ? 3 : 0}
          ry={height > 0 ? 3 : 0}
        />
      );
    }

    return (
      <path
        d={`
          M${x},${y + height}
          L${x},${y + radius}
          Q${x},${y} ${x + radius},${y}
          L${x + width - radius},${y}
          Q${x + width},${y} ${x + width},${y + radius}
          L${x + width},${y + height}
          Z
        `}
        fill={fill}
      />
    );
  };

  const COLLECTED_COLOR = "#52c41a";
  const CLAIMED_COLOR = "#f5222d";

  const periodLabel: Record<string, string> = {
    last7: "Last 7 Days",
    last14: "Last 14 Days",
    last30: "Last 30 Days",
    last90: "Last 90 Days",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    thisYear: "This Year",
  };

  useEffect(() => {
    setActive(true);
    getDashboardReportApi(datePeriod, currentGroup)
      .then((res: any) => {
        const d = res.data;

        setTotalSales(d.totalSales ?? 0);
        setSalesChange(d.totalSalesChange ?? 0);
        setActivePolicyCount(d.activePoliciesCount ?? 0);
        setPolicyChange(d.activePoliciesChange ?? 0);
        setActiveUserCount(d.activeUsersCount ?? 0);
        setActiveProductCount(d.activeProductsCount ?? 0);

        // Daily policies + collected amount → bar charts (shared bucketing)
        const monthLabels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        type DailyItem = { date: string; count: number; paidCount?: number; collectedAmount?: number };
        type DailyClaimItem = { date: string; internalCollected?: number; internalClaimed?: number; externalCollected?: number; externalClaimed?: number };
        const rawDaily = (d.dailyPolicies ?? []) as DailyItem[];
        const rawClaims = (d.claimsChart?.daily ?? []) as DailyClaimItem[];

        let bar: { name: string; value: number; paid: number; key?: string }[];
        let collections: { name: string; amount: number; key?: string }[];
        let claims: { name: string; internalCollected: number; internalClaimed: number; externalCollected: number; externalClaimed: number }[];

        if (datePeriod === "thisYear") {
          const monthlyTotals = new Array(12).fill(0);
          const monthlyPaid = new Array(12).fill(0);
          const monthlyAmount = new Array(12).fill(0);
          rawDaily.forEach((item) => {
            const dt = new Date(item.date);
            const m = dt.getUTCMonth();
            monthlyTotals[m] += item.count;
            monthlyPaid[m] += item.paidCount ?? 0;
            monthlyAmount[m] += Math.abs(item.collectedAmount ?? 0);
          });
          bar = monthLabels.map((label, idx) => ({
            name: label,
            value: monthlyTotals[idx],
            paid: monthlyPaid[idx],
            key: `month-${idx}`,
          }));
          collections = monthLabels.map((label, idx) => ({
            name: label,
            amount: monthlyAmount[idx],
            key: `month-${idx}`,
          }));

          const cIntCol = new Array(12).fill(0);
          const cIntCl = new Array(12).fill(0);
          const cExtCol = new Array(12).fill(0);
          const cExtCl = new Array(12).fill(0);
          rawClaims.forEach((item) => {
            const m = new Date(item.date).getUTCMonth();
            cIntCol[m] += Math.abs(item.internalCollected ?? 0);
            cIntCl[m]  += Math.abs(item.internalClaimed ?? 0);
            cExtCol[m] += Math.abs(item.externalCollected ?? 0);
            cExtCl[m]  += Math.abs(item.externalClaimed ?? 0);
          });
          claims = monthLabels.map((label, idx) => ({
            name: label,
            internalCollected: cIntCol[idx],
            internalClaimed: cIntCl[idx],
            externalCollected: cExtCol[idx],
            externalClaimed: cExtCl[idx],
          }));
        } else if (datePeriod === "last90") {
          const weekMap = new Map<string, { name: string; value: number; paid: number; amount: number; intCol: number; intCl: number; extCol: number; extCl: number; sortKey: number }>();
          const bucketFor = (dt: Date) => {
            const day = dt.getUTCDay();
            const diffToMonday = (day + 6) % 7;
            const weekStart = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() - diffToMonday));
            const weekEnd = new Date(weekStart);
            weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
            const key = weekStart.toISOString().slice(0, 10);
            const sameMonth = weekStart.getUTCMonth() === weekEnd.getUTCMonth();
            const label = sameMonth
              ? `${weekStart.getUTCDate()}-${weekEnd.getUTCDate()} ${monthLabels[weekStart.getUTCMonth()]}`
              : `${weekStart.getUTCDate()} ${monthLabels[weekStart.getUTCMonth()]} - ${weekEnd.getUTCDate()} ${monthLabels[weekEnd.getUTCMonth()]}`;
            return { key, label, sortKey: weekStart.getTime() };
          };
          rawDaily.forEach((item) => {
            const { key, label, sortKey } = bucketFor(new Date(item.date));
            const amt = Math.abs(item.collectedAmount ?? 0);
            const existing = weekMap.get(key);
            if (existing) {
              existing.value += item.count;
              existing.paid += item.paidCount ?? 0;
              existing.amount += amt;
            } else {
              weekMap.set(key, { name: label, value: item.count, paid: item.paidCount ?? 0, amount: amt, intCol: 0, intCl: 0, extCol: 0, extCl: 0, sortKey });
            }
          });
          rawClaims.forEach((item) => {
            const { key, label, sortKey } = bucketFor(new Date(item.date));
            const existing = weekMap.get(key);
            const intCol = Math.abs(item.internalCollected ?? 0);
            const intCl  = Math.abs(item.internalClaimed ?? 0);
            const extCol = Math.abs(item.externalCollected ?? 0);
            const extCl  = Math.abs(item.externalClaimed ?? 0);
            if (existing) {
              existing.intCol += intCol;
              existing.intCl += intCl;
              existing.extCol += extCol;
              existing.extCl += extCl;
            } else {
              weekMap.set(key, { name: label, value: 0, paid: 0, amount: 0, intCol, intCl, extCol, extCl, sortKey });
            }
          });
          const sorted = Array.from(weekMap.values()).sort((a, b) => a.sortKey - b.sortKey);
          bar = sorted.map(({ name, value, paid }, idx) => ({
            name,
            value,
            paid,
            key: `week-${idx}`,
          }));
          collections = sorted.map(({ name, amount }, idx) => ({
            name,
            amount,
            key: `week-${idx}`,
          }));
          claims = sorted.map(({ name, intCol, intCl, extCol, extCl }) => ({
            name,
            internalCollected: intCol,
            internalClaimed: intCl,
            externalCollected: extCol,
            externalClaimed: extCl,
          }));
        } else {
          bar = [];
          collections = [];
          const claimsMap = new Map<string, { name: string; internalCollected: number; internalClaimed: number; externalCollected: number; externalClaimed: number; sortKey: number }>();
          rawDaily.forEach((item) => {
            const dt = new Date(item.date);
            const label = `${dt.getUTCDate()} ${monthLabels[dt.getUTCMonth()]}`;
            const key = item.date?.slice?.(0, 10) || label;
            bar.push({ name: label, value: item.count, paid: item.paidCount ?? 0, key });
            collections.push({
              name: label,
              amount: Math.abs(item.collectedAmount ?? 0),
              key,
            });
            if (!claimsMap.has(label)) {
              claimsMap.set(label, { name: label, internalCollected: 0, internalClaimed: 0, externalCollected: 0, externalClaimed: 0, sortKey: dt.getTime() });
            }
          });
          rawClaims.forEach((item) => {
            const dt = new Date(item.date);
            const label = `${dt.getUTCDate()} ${monthLabels[dt.getUTCMonth()]}`;
            const existing = claimsMap.get(label);
            const intCol = Math.abs(item.internalCollected ?? 0);
            const intCl  = Math.abs(item.internalClaimed ?? 0);
            const extCol = Math.abs(item.externalCollected ?? 0);
            const extCl  = Math.abs(item.externalClaimed ?? 0);
            if (existing) {
              existing.internalCollected += intCol;
              existing.internalClaimed += intCl;
              existing.externalCollected += extCol;
              existing.externalClaimed += extCl;
            } else {
              claimsMap.set(label, { name: label, internalCollected: intCol, internalClaimed: intCl, externalCollected: extCol, externalClaimed: extCl, sortKey: dt.getTime() });
            }
          });
          claims = Array.from(claimsMap.values())
            .sort((a, b) => a.sortKey - b.sortKey)
            .map(({ name, internalCollected, internalClaimed, externalCollected, externalClaimed }) => ({
              name, internalCollected, internalClaimed, externalCollected, externalClaimed,
            }));
        }
        setBarData(bar);
        setCollectionsData(collections);
        setClaimsData(claims);

        // Age group → pie chart
        const ag = d.policiesPerAgeGroup ?? {};
        const ageBuckets: { name: string; value: number; key: string }[] = [
          { name: "Under 55", value: ag.under55 ?? 0, key: "under55" },
          { name: "Under 65", value: ag.under65 ?? 0, key: "under65" },
          { name: "Under 75", value: ag.under75 ?? 0, key: "under75" },
          { name: "Under 85", value: ag.under85 ?? 0, key: "under85" },
          { name: "Above 85", value: ag.above85 ?? 0, key: "above85" },
        ];
        setPieData(ageBuckets);

        // Cover amount → pie chart
        const ca = d.policiesPerCoverAmount ?? {};
        const coverBuckets: { name: string; value: number; key: string }[] = [
          { name: "R0 - R5k", value: ca["0-5000"] ?? 0, key: "0-5000" },
          { name: "R5k - R10k", value: ca["5001-10000"] ?? 0, key: "5001-10000" },
          { name: "R10k - R15k", value: ca["10001-15000"] ?? 0, key: "10001-15000" },
          { name: "R15k - R20k", value: ca["15001-20000"] ?? 0, key: "15001-20000" },
          { name: "R20k - R25k", value: ca["20001-25000"] ?? 0, key: "20001-25000" },
          { name: "R25k - R30k", value: ca["25001-30000"] ?? 0, key: "25001-30000" },
          { name: "R30k+", value: ca["30001+"] ?? 0, key: "30001+" },
        ];
        setCoverAmountPieData(coverBuckets);

        // Sales by group — BUG-24: with groupId + includeSubgroups, BE returns
        // own group + all descendants; without it, owning Level 1 rollup.
        const rawByGroup = Array.isArray(d.salesByGroup) ? d.salesByGroup : [];
        setHasSalesByGroup(rawByGroup.length > 0);
        setSalesByGroupData(mapSalesByGroupRows(rawByGroup));

        // Top products table
        const ranked = (d.topProducts ?? []).map((p: any, index: number) => ({
          key: p.id,
          score: index + 1,
          name: p.name,
          price: `R${p.policyPremium}`,
          cover: `R${p.coverAmount}`,
          qty: p.sales,
          volume: `R${p.salesVolume}`,
          margin: `${p.profitMargin}%`,
        }));
        setTopProducts(ranked);

        setActive(false);
      })
      .catch((err: any) => {
        console.log(err);
        setActive(false);
      });
  }, [currentGroup, datePeriod]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <SideBar />
      <Layout className="site-layout">
        <TopBar />
        <Content style={{ overflowX: 'hidden' }}>
          <div
            style={{
              padding: window.innerWidth < 768 ? 16 : 24,
              minHeight: "100%",
              maxWidth: '100%',
              overflowX: 'hidden'
            }}
          >
            {isActive ? (
              <DashboardSkeleton />
            ) : (
              <>
                <div style={{ 
                  marginBottom: window.innerWidth < 768 ? 16 : 24, 
                  display: 'flex', 
                  alignItems: 'center',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: window.innerWidth < 768 ? 12 : 16,
                  flexWrap: 'wrap'
                }}>
                  <DashboardOutlined
                    style={{
                      fontSize: window.innerWidth < 768 ? 24 : 28,
                      marginRight: window.innerWidth < 768 ? 8 : 12,
                      color: "#f04f05",
                    }}
                  />
                  <AntTitle level={window.innerWidth < 768 ? 3 : 2} style={{ margin: 0 }}>Dashboard</AntTitle>
                </div>

              <Row gutter={[window.innerWidth < 576 ? 8 : 16, window.innerWidth < 576 ? 8 : 16]} style={{ marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
                <Col span={24}>
                  <Card style={{ 
                    border: "1px solid #e8e8e8", 
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: window.innerWidth < 768 ? 14 : 15 }}>Filter by Period:</span>
                      <Select
                        value={datePeriod}
                        onChange={(value) => setDatePeriod(value)}
                        style={{ width: window.innerWidth < 576 ? '100%' : 200 }}
                        size="large"
                        options={[
                          { label: "Last 7 Days", value: "last7" },
                          { label: "Last 14 Days", value: "last14" },
                          { label: "Last 30 Days", value: "last30" },
                          { label: "Last 90 Days", value: "last90" },
                          { label: "This Month", value: "thisMonth" },
                          { label: "Last Month", value: "lastMonth" },
                          { label: "This Year", value: "thisYear" },
                        ]}
                      />
                    </div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={[window.innerWidth < 576 ? 8 : 16, window.innerWidth < 576 ? 8 : 16]}>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f6ffed 100%)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <DollarOutlined style={{ fontSize: window.innerWidth < 576 ? 20 : 24, color: '#52c41a', marginRight: 8, flexShrink: 0 }} />
                      <Text style={{ fontSize: window.innerWidth < 576 ? 12 : 14, fontWeight: 500, color: '#8c8c8c' }}>Total Sales</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <AntTitle level={3} style={{ margin: 0 }}>
                        R <AnimatedNumber value={totalSales} />
                      </AntTitle>
                      <Text type={salesChange < 0 ? "danger" : "success"} style={{ fontSize: 13 }}>
                        {salesChange < 0 ? "▼" : "▲"}{" "}
                        {Math.abs(salesChange).toFixed(2)}%
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #e6f7ff 100%)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <UserOutlined style={{ fontSize: window.innerWidth < 576 ? 20 : 24, color: '#1890ff', marginRight: 8, flexShrink: 0 }} />
                      <Text style={{ fontSize: window.innerWidth < 576 ? 12 : 14, fontWeight: 500, color: '#8c8c8c' }}>Active Users</Text>
                    </div>
                    <AntTitle level={3} style={{ margin: 0 }}>
                      <AnimatedNumber value={activeUserCount} />
                    </AntTitle>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #fff7e6 100%)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <FileProtectOutlined style={{ fontSize: window.innerWidth < 576 ? 20 : 24, color: '#faad14', marginRight: 8, flexShrink: 0 }} />
                      <Text style={{ fontSize: window.innerWidth < 576 ? 12 : 14, fontWeight: 500, color: '#8c8c8c' }}>Active Policies</Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <AntTitle level={3} style={{ margin: 0 }}>
                        <AnimatedNumber value={activePolicyCount} />
                      </AntTitle>
                      <Text type={policyChange < 0 ? "danger" : "success"} style={{ fontSize: 13 }}>
                        {policyChange < 0 ? "▼" : "▲"}{" "}
                        {Math.abs(policyChange).toFixed(2)}%
                      </Text>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={12} lg={6}>
                  <Card
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f9f0ff 100%)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <ShoppingOutlined style={{ fontSize: window.innerWidth < 576 ? 20 : 24, color: '#722ed1', marginRight: 8, flexShrink: 0 }} />
                      <Text style={{ fontSize: window.innerWidth < 576 ? 12 : 14, fontWeight: 500, color: '#8c8c8c' }}>Active Products</Text>
                    </div>
                    <AntTitle level={3} style={{ margin: 0 }}>
                      <AnimatedNumber value={activeProductCount} />
                    </AntTitle>
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    title={`Daily Policies (${periodLabel[datePeriod] ?? datePeriod})`}
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 250 : 300}>
                      <BarChart data={barData}>
                        <XAxis
                          dataKey="name"
                          interval={window.innerWidth < 576 ? 'preserveStartEnd' : 0}
                          angle={window.innerWidth < 768 ? -45 : -40}
                          textAnchor="end"
                          height={window.innerWidth < 576 ? 80 : 60}
                          tick={{ fontSize: window.innerWidth < 576 ? 10 : 12 }}
                        />
                        <YAxis />
                        <Tooltip
                          cursor={{ fill: "transparent" }}
                          formatter={(v: number, _n: string, props: any) => {
                            const total = barData.reduce((s, d) => s + d.value, 0);
                            const pct = pctOf(Number(v), total);
                            return [
                              `${v} (${pct.toFixed(1)}% of period)`,
                              props?.payload?.name ?? "Policies",
                            ];
                          }}
                        />
                        <Bar
                          dataKey="value"
                          name="Policies"
                          fill="#1890ff"
                          shape={<CustomRoundedBar />}
                          isAnimationActive={false}
                          cursor="pointer"
                          onClick={(data: any) => {
                            const row = data?.payload ?? data;
                            if (!row) return;
                            const total = barData.reduce((s, d) => s + d.value, 0);
                            openChartDrilldown({
                              chart: "day",
                              key: String(row.key ?? row.name),
                              label: row.name,
                              count: Number(row.value ?? 0),
                              total,
                              title: `Daily Policies — ${row.name}`,
                            });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    title={`Paid Policies Value (${periodLabel[datePeriod] ?? datePeriod})`}
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 250 : 300}>
                      <BarChart data={collectionsData}>
                        <XAxis
                          dataKey="name"
                          interval={window.innerWidth < 576 ? 'preserveStartEnd' : 0}
                          angle={window.innerWidth < 768 ? -45 : -40}
                          textAnchor="end"
                          height={window.innerWidth < 576 ? 80 : 60}
                          tick={{ fontSize: window.innerWidth < 576 ? 10 : 12 }}
                        />
                        <YAxis
                          tickFormatter={(v: number) =>
                            v >= 1000
                              ? `R${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`
                              : `R${v.toLocaleString()}`
                          }
                        />
                        <Tooltip
                          cursor={{ fill: "transparent" }}
                          formatter={(v: number) => {
                            const total = collectionsData.reduce(
                              (s, d) => s + d.amount,
                              0
                            );
                            const pct = pctOf(Number(v), total);
                            return `R${Number(v).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} (${pct.toFixed(1)}%)`;
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="amount"
                          name="Paid Policies Value"
                          fill="#52c41a"
                          shape={<CustomRoundedBar />}
                          isAnimationActive={false}
                          cursor="pointer"
                          onClick={(data: any) => {
                            const row = data?.payload ?? data;
                            if (!row) return;
                            const total = collectionsData.reduce(
                              (s, d) => s + d.amount,
                              0
                            );
                            openChartDrilldown({
                              chart: "collections",
                              key: String(row.key ?? row.name),
                              label: row.name,
                              count: Number(row.amount ?? 0),
                              total,
                              extra: `R${Number(row.amount ?? 0).toLocaleString()}`,
                              title: `Paid Policies Value — ${row.name}`,
                            });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={24} lg={24}>
                  <Card
                    title={`Claims: Collected vs Claimed Value (${periodLabel[datePeriod] ?? datePeriod})`}
                    style={{
                      border: "1px solid #e8e8e8",
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    {(() => {
                      const SERIES: { key: keyof typeof claimsData[number]; label: string; color: string; stackId: "collected" | "claimed" }[] = [
                        { key: "externalCollected", label: "External Collected", color: "#73d13d", stackId: "collected" },
                        { key: "internalCollected", label: "Internal Collected", color: "#237804", stackId: "collected" },
                        { key: "externalClaimed",   label: "External Claimed",   color: "#ff7875", stackId: "claimed" },
                        { key: "internalClaimed",   label: "Internal Claimed",   color: "#a8071a", stackId: "claimed" },
                      ];
                      const fmt = (n: number) =>
                        `R${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      const handleLegendClick = (e: any) => {
                        const id = e?.dataKey ?? e?.id;
                        if (!id) return;
                        setHiddenClaimSeries((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id); else next.add(id);
                          return next;
                        });
                      };
                      const totals = SERIES.reduce<Record<string, number>>((acc, s) => {
                        acc[s.key] = hiddenClaimSeries.has(s.key)
                          ? 0
                          : claimsData.reduce((sum, d) => sum + (d[s.key] as number), 0);
                        return acc;
                      }, {});
                      const visibleCollected = totals.internalCollected + totals.externalCollected;
                      const visibleClaimed   = totals.internalClaimed   + totals.externalClaimed;
                      const diff = visibleCollected - visibleClaimed;
                      const diffColor = diff < 0 ? CLAIMED_COLOR : diff > 0 ? COLLECTED_COLOR : '#8c8c8c';
                      const diffLabel = diff < 0 ? `-${fmt(Math.abs(diff))}` : diff > 0 ? `+${fmt(diff)}` : fmt(0);
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 280 : 340}>
                            <BarChart data={claimsData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }} barCategoryGap="20%">
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                              <XAxis
                                dataKey="name"
                                interval={window.innerWidth < 576 ? 'preserveStartEnd' : 0}
                                angle={window.innerWidth < 768 ? -45 : -40}
                                textAnchor="end"
                                height={window.innerWidth < 576 ? 80 : 60}
                                tick={{ fontSize: window.innerWidth < 576 ? 10 : 12 }}
                              />
                              <YAxis
                                tickFormatter={(v: number) =>
                                  v >= 1000
                                    ? `R${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`
                                    : `R${v.toLocaleString()}`
                                }
                              />
                              <Tooltip
                                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                content={({ active, payload, label }) => {
                                  if (!active || !payload || !payload.length) return null;
                                  const row: any = payload[0]?.payload ?? {};
                                  const colTotal =
                                    (hiddenClaimSeries.has("internalCollected") ? 0 : Number(row.internalCollected ?? 0)) +
                                    (hiddenClaimSeries.has("externalCollected") ? 0 : Number(row.externalCollected ?? 0));
                                  const clmTotal =
                                    (hiddenClaimSeries.has("internalClaimed") ? 0 : Number(row.internalClaimed ?? 0)) +
                                    (hiddenClaimSeries.has("externalClaimed") ? 0 : Number(row.externalClaimed ?? 0));
                                  const d = colTotal - clmTotal;
                                  const dColor = d < 0 ? CLAIMED_COLOR : d > 0 ? COLLECTED_COLOR : '#8c8c8c';
                                  const dLabel = d < 0 ? `-${fmt(Math.abs(d))}` : d > 0 ? `+${fmt(d)}` : fmt(0);
                                  return (
                                    <div style={{
                                      background: '#fff',
                                      border: '1px solid #f0f0f0',
                                      borderRadius: 6,
                                      padding: '8px 12px',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                      fontSize: 13,
                                    }}>
                                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                                      {payload.map((p: any) => (
                                        <div key={p.dataKey} style={{ color: p.color }}>
                                          ● {p.name}: {fmt(Number(p.value ?? 0))}
                                        </div>
                                      ))}
                                      <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 4, paddingTop: 4 }}>
                                        <div style={{ color: COLLECTED_COLOR }}>Collected total: <strong>{fmt(colTotal)}</strong></div>
                                        <div style={{ color: CLAIMED_COLOR }}>Claimed total: <strong>{fmt(clmTotal)}</strong></div>
                                        <div style={{ color: dColor, fontWeight: 600 }}>Net: {dLabel}</div>
                                      </div>
                                    </div>
                                  );
                                }}
                              />
                              <Legend
                                onClick={handleLegendClick}
                                wrapperStyle={{ cursor: 'pointer', userSelect: 'none' }}
                                formatter={(value, entry: any) => {
                                  const hidden = hiddenClaimSeries.has(entry?.dataKey);
                                  return (
                                    <span style={{
                                      color: hidden ? '#bfbfbf' : '#595959',
                                      textDecoration: hidden ? 'line-through' : 'none',
                                    }}>{value}</span>
                                  );
                                }}
                              />
                              {SERIES.map((s) => (
                                <Bar
                                  key={s.key}
                                  dataKey={s.key}
                                  name={s.label}
                                  stackId={s.stackId}
                                  fill={s.color}
                                  hide={hiddenClaimSeries.has(s.key)}
                                  isAnimationActive={false}
                                  radius={s.key === "internalCollected" || s.key === "internalClaimed" ? [4, 4, 0, 0] : 0}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                          {/* Period summary (reflects visible bars) */}
                          <div style={{
                            display: 'flex',
                            gap: 18,
                            flexWrap: 'wrap',
                            marginTop: 12,
                            padding: '10px 14px',
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            fontSize: 13,
                            width: 'fit-content',
                          }}>
                            <span style={{ color: '#595959', fontWeight: 500 }}>
                              Period Total
                            </span>
                            {SERIES.map((s) => (
                              <span
                                key={s.key}
                                style={{
                                  color: hiddenClaimSeries.has(s.key) ? '#bfbfbf' : s.color,
                                  textDecoration: hiddenClaimSeries.has(s.key) ? 'line-through' : 'none',
                                }}
                              >
                                ● {s.label}: <strong>{fmt(totals[s.key])}</strong>
                              </span>
                            ))}
                            <span style={{ color: COLLECTED_COLOR, borderLeft: '1px solid #e8e8e8', paddingLeft: 16 }}>
                              Collected: <strong>{fmt(visibleCollected)}</strong>
                            </span>
                            <span style={{ color: CLAIMED_COLOR }}>
                              Claimed: <strong>{fmt(visibleClaimed)}</strong>
                            </span>
                            <span style={{ color: diffColor, borderLeft: '1px solid #e8e8e8', paddingLeft: 16 }}>
                              Net (Collected - Claimed): <strong>{diffLabel}</strong>
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={24} lg={12}>
                  <Card
                    title="Policies Per Age Group"
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 250 : 300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={window.innerWidth < 768 ? 60 : 80}
                          label={pieLabel(
                            pieData.reduce((s, d) => s + d.value, 0)
                          )}
                          style={{ cursor: "pointer" }}
                          onClick={(_: any, index: number) => {
                            const row = pieData[index];
                            if (!row) return;
                            openPieBucketDrilldown(
                              "age",
                              pieData,
                              row.name,
                              "Age Group"
                            );
                          }}
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={pieColors[index % pieColors.length]}
                            />
                          ))}
                        </Pie>
                        <Legend
                          wrapperStyle={{ cursor: "pointer", userSelect: "none" }}
                          onClick={(e: any) => {
                            const name = e?.value ?? e?.payload?.value;
                            if (!name) return;
                            openPieBucketDrilldown(
                              "age",
                              pieData,
                              String(name),
                              "Age Group"
                            );
                          }}
                        />
                        <Tooltip
                          formatter={(v: number, name: string) => {
                            const total = pieData.reduce(
                              (s, d) => s + d.value,
                              0
                            );
                            return [
                              `${v} (${pctOf(Number(v), total).toFixed(1)}%)`,
                              name,
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>

                <Col xs={24} sm={24} md={24} lg={12}>
                  <Card
                    title="Policies Per Cover Amounts"
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 250 : 300}>
                      <PieChart>
                        <Pie
                          data={coverAmountPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={window.innerWidth < 768 ? 60 : 80}
                          label={pieLabel(
                            coverAmountPieData.reduce((s, d) => s + d.value, 0)
                          )}
                          style={{ cursor: "pointer" }}
                          onClick={(_: any, index: number) => {
                            const row = coverAmountPieData[index];
                            if (!row) return;
                            openPieBucketDrilldown(
                              "cover",
                              coverAmountPieData,
                              row.name,
                              "Cover Amount"
                            );
                          }}
                        >
                          {coverAmountPieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={pieColors[index % pieColors.length]}
                            />
                          ))}
                        </Pie>
                        <Legend
                          wrapperStyle={{ cursor: "pointer", userSelect: "none" }}
                          onClick={(e: any) => {
                            const name = e?.value ?? e?.payload?.value;
                            if (!name) return;
                            openPieBucketDrilldown(
                              "cover",
                              coverAmountPieData,
                              String(name),
                              "Cover Amount"
                            );
                          }}
                        />
                        <Tooltip
                          formatter={(v: number, name: string) => {
                            const total = coverAmountPieData.reduce(
                              (s, d) => s + d.value,
                              0
                            );
                            return [
                              `${v} (${pctOf(Number(v), total).toFixed(1)}%)`,
                              name,
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </Col>

                <Col span={24}>
                  <Card
                    title="Sales by Group"
                    style={{
                      border: "1px solid #e8e8e8",
                      borderRadius: 12,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                    headStyle={{
                      borderBottom: "2px solid #f0f0f0",
                      fontWeight: 600,
                    }}
                  >
                    {!hasSalesByGroup ? (
                      <Empty description="No sales by group for this period" />
                    ) : (
                      <ResponsiveContainer
                        width="100%"
                        height={Math.min(
                          520,
                          Math.max(
                            window.innerWidth < 768 ? 250 : 300,
                            80 + salesByGroupData.length * 36
                          )
                        )}
                      >
                        <BarChart data={salesByGroupData}>
                          <XAxis
                            dataKey="name"
                            interval={0}
                            angle={-30}
                            textAnchor="end"
                            height={Math.min(
                              120,
                              60 + Math.min(salesByGroupData.length, 8) * 6
                            )}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis yAxisId="left" />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={(v: number) =>
                              v >= 1000
                                ? `R${(v / 1000).toLocaleString(undefined, {
                                    maximumFractionDigits: 1,
                                  })}k`
                                : `R${v}`
                            }
                          />
                          <Tooltip
                            formatter={(v: number, name: string) => {
                              if (name === "Sales amount") {
                                const total = salesByGroupData.reduce(
                                  (s, d) => s + d.salesAmount,
                                  0
                                );
                                return `R${Number(v).toLocaleString()} (${pctOf(
                                  Number(v),
                                  total
                                ).toFixed(1)}%)`;
                              }
                              const total = salesByGroupData.reduce(
                                (s, d) => s + d.policyCount,
                                0
                              );
                              return `${v} (${pctOf(Number(v), total).toFixed(
                                1
                              )}%)`;
                            }}
                          />
                          <Legend />
                          <Bar
                            yAxisId="left"
                            dataKey="policyCount"
                            name="Policies"
                            fill="#1890ff"
                            cursor="pointer"
                            onClick={(data: any) => {
                              const row = data?.payload ?? data;
                              if (!row) return;
                              const total = salesByGroupData.reduce(
                                (s, d) => s + d.policyCount,
                                0
                              );
                              openChartDrilldown({
                                chart: "group",
                                key: String(row.key),
                                label: row.name,
                                count: Number(row.policyCount ?? 0),
                                total,
                                extra: `R${Number(
                                  row.salesAmount ?? 0
                                ).toLocaleString()}`,
                                title: `Sales by Group — ${row.name}`,
                              });
                            }}
                          />
                          <Bar
                            yAxisId="right"
                            dataKey="salesAmount"
                            name="Sales amount"
                            fill="#52c41a"
                            cursor="pointer"
                            onClick={(data: any) => {
                              const row = data?.payload ?? data;
                              if (!row) return;
                              const total = salesByGroupData.reduce(
                                (s, d) => s + d.policyCount,
                                0
                              );
                              openChartDrilldown({
                                chart: "group",
                                key: String(row.key),
                                label: row.name,
                                count: Number(row.policyCount ?? 0),
                                total,
                                extra: `R${Number(
                                  row.salesAmount ?? 0
                                ).toLocaleString()}`,
                                title: `Sales by Group — ${row.name}`,
                              });
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </Col>

                <Col span={24}>
                  <Card
                    title="Top Products"
                    style={{ 
                      border: "1px solid #e8e8e8", 
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    headStyle={{ borderBottom: '2px solid #f0f0f0', fontWeight: 600 }}
                  >
                    <Table
                      columns={productColumns}
                      dataSource={topProducts}
                      pagination={false}
                      scroll={{ x: window.innerWidth < 768 ? 800 : undefined }}
                    />
                  </Card>
                </Col>
              </Row>

              <ChartDrilldownModal
                show={drilldownOpen}
                onClose={() => setDrilldownOpen(false)}
                title={drilldownTitle}
                summary={drilldownSummary}
                rows={drilldownRows}
                loading={drilldownLoading}
                apiError={drilldownError}
              />

              <Modal
                title="Welcome to LifeCycle"
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                  <Button key="startTour" type="primary" onClick={handleOk}>
                    Begin Tour
                  </Button>,
                  <Button key="endTour" onClick={handleCancel}>
                    No Thanks
                  </Button>,
                ]}
              >
                {/* Add your image here */}
                <div className="vapor-img"></div>
                {/* Add any additional content here */}
                <p>
                  Welcome to our application! This tour will guide you through
                  the key features.
                </p>
              </Modal>
              </>
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardPage;
